import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { getPortfolioPropertySummary } from "@/api/properties";
import { useIsInternalUser } from "@/hooks/useIsInternalUser";
import useAuth from "@/hooks/useAuth";
import {
  ALL_CAPABILITIES,
  capabilitiesForProperty,
  capabilitiesForScopes,
  type Capability,
} from "@/types/entitlements";

export interface EntitlementsValue {
  /** True until the portfolio's deal toggles have resolved. Nav gating waits on this. */
  loading: boolean;
  /** Union of capabilities across the whole portfolio — what the nav gates on. */
  capabilities: readonly Capability[];
  hasSales: boolean;
  hasRevenue: boolean;
  /** Per-property capabilities from the deal toggles. Absent = not yet known. */
  capabilitiesById: Record<string, readonly Capability[]>;
  capabilitiesFor: (propertyId: string | undefined) => readonly Capability[];
}

const INTERNAL_VALUE: EntitlementsValue = {
  loading: false,
  capabilities: ALL_CAPABILITIES,
  hasSales: true,
  hasRevenue: true,
  capabilitiesById: {},
  capabilitiesFor: () => ALL_CAPABILITIES,
};

export const EntitlementsContext =
  createContext<EntitlementsValue>(INTERNAL_VALUE);

/**
 * Resolves what the signed-in user's portfolio entitles them to.
 *
 * Internal users are never gated: their portfolio response is the entire active
 * estate, so gating it would be meaningless.
 */
export function EntitlementsProvider({ children }: { children: ReactNode }) {
  const isInternal = useIsInternalUser();
  const { isAuthenticated, isInitialized, user } = useAuth();
  const orgScopes = user?.user_org_scopes;
  // The token's org scopes cap what deal toggles can grant an external user.
  const scopeCapabilities = useMemo(
    () => capabilitiesForScopes(orgScopes),
    [orgScopes]
  );

  // "pending" until entitlements are genuinely known. Settling early is not a
  // cosmetic difference: every consumer — the post-login redirect, both route
  // guards, the sidebar filter — acts the moment this reads ready, so a
  // premature settle publishes a fabricated "resolved, nothing known" state
  // and the landing page becomes a race against the fetch.
  const [status, setStatus] = useState<"pending" | "ready">("pending");
  const [capabilitiesById, setCapabilitiesById] = useState<
    Record<string, readonly Capability[]>
  >({});
  const hasFetched = useRef(false);
  const loading = status === "pending";

  useEffect(() => {
    // Auth hasn't resolved yet, so we can't even tell which branch applies —
    // stay pending rather than answering for a user we don't know yet.
    if (!isInitialized) return;

    // Internal users need no lookup, and an unauthenticated session has no
    // portfolio to read — both are genuinely resolved with no fetch.
    if (!isAuthenticated || isInternal) {
      setStatus("ready");
      // Released so the next sign-in fetches again. Keycloak signs out without
      // a page reload, so a ref left set would silently serve the previous
      // user's entitlements to whoever logs in next.
      hasFetched.current = false;
      return;
    }
    if (hasFetched.current) return;
    // Both reset before the request, not after: leaving status "ready" over the
    // previous user's map would let the guards and the sidebar decide access
    // from stale data while the fetch is still in flight.
    setStatus("pending");
    setCapabilitiesById({});
    hasFetched.current = true;

    let cancelled = false;

    const load = async () => {
      try {
        const response = await getPortfolioPropertySummary();
        const next: Record<string, readonly Capability[]> = {};
        for (const p of response?.portfolioPropertySummaries ?? []) {
          next[p.propertyId] = capabilitiesForProperty(p);
        }
        if (cancelled) return;
        setCapabilitiesById(next);
      } catch (err) {
        // Portfolio itself failed — leave the map empty, which grants
        // everything rather than locking the user out of the whole app.
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.debug("[entitlements] portfolio lookup failed", err);
        }
        if (!cancelled) setCapabilitiesById({});
      } finally {
        if (!cancelled) setStatus("ready");
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isInternal, isAuthenticated, isInitialized]);

  const capabilitiesFor = useCallback(
    (propertyId: string | undefined): readonly Capability[] => {
      if (isInternal) return ALL_CAPABILITIES;
      // A property outside the resolved map (internal-visible, or not yet
      // loaded) is unknown rather than unentitled — falls open to the scopes.
      const propertyCapabilities =
        propertyId && propertyId in capabilitiesById
          ? capabilitiesById[propertyId]
          : ALL_CAPABILITIES;
      return propertyCapabilities.filter((c) => scopeCapabilities.has(c));
    },
    [isInternal, capabilitiesById, scopeCapabilities]
  );

  const value = useMemo<EntitlementsValue>(() => {
    if (isInternal) return INTERNAL_VALUE;

    const resolved = Object.values(capabilitiesById);

    // Nothing resolved at all — the portfolio call failed, or it answered
    // before the map was populated. Iterating an empty map would grant nothing
    // and strand a user who has properties on the no-access page, so this
    // falls open like every other unknown in this file. Only a portfolio whose
    // toggles we positively know, none of which grant anything, is treated
    // as unentitled.
    const granted = new Set<Capability>(
      resolved.length === 0 ? ALL_CAPABILITIES : resolved.flat()
    );
    // Scopes fail closed: an external user with none sees no tabs at all.
    const capabilities = ALL_CAPABILITIES.filter(
      (c) => granted.has(c) && scopeCapabilities.has(c)
    );

    return {
      loading,
      capabilities,
      hasSales: capabilities.includes("sales"),
      hasRevenue: capabilities.includes("revenue"),
      capabilitiesById,
      capabilitiesFor,
    };
  }, [
    isInternal,
    loading,
    capabilitiesById,
    capabilitiesFor,
    scopeCapabilities,
  ]);

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
}
